import { EventEmitter, Inject, InjectionToken, OnDestroy, Output } from '@angular/core';

const POPUP_CONTENT = new InjectionToken<any>('PopupContent');

import {
  Component,
  Input,
  ElementRef,
  TemplateRef,
  ContentChild,
  Injector
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { of } from 'rxjs';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-link-with-popup-content',
  template: `
    <div class="link-with-popup-content popup-content">
      <div class="popup-header">
        <h3>{{headerText}}</h3>
        <button (click)="close()" class="close-btn">&times;</button>
      </div>
      <div class="popup-body">
          <div *ngIf="isLoading | async">
          <mat-spinner diameter="30"></mat-spinner>
        </div>
        <ng-container *ngIf="!(isLoading | async)">
          <ng-container *ngTemplateOutlet="content?.template">
          </ng-container>
        </ng-container>
      </div>
    </div>
  `,
  styles: [`
    :host {
      font-family: var(--site-wide-theme-font-family);
      font-size: var(--site-wide-theme-font-size);
    }
    ::ng-deep .link-with-popup-content.popup-content {
      background: white ;
      border: 1px solid #ccc;
      border-radius: 4px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      min-width: 200px;
    }

    ::ng-deep .link-with-popup-content.popup-content .popup-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px;
      border-bottom: 1px solid #eee;
    }
    ::ng-deep .link-with-popup-content.popup-content .popup-header h3 {
      font-size: 1rem;
    }

    ::ng-deep .link-with-popup-content.popup-content .popup-body {
      padding: 10px;
      overflow-y: auto;
      overflow-x: hidden;
      max-height: 300px;
      font-size: .8rem;
    }

    ::ng-deep .link-with-popup-content.popup-content .close-btn {
      border: none;
      background: none;
      font-size: 20px;
      cursor: pointer;
      padding: 0 5px;
    }
  `],
  //encapsulation: ViewEncapsulation.None
})
export class LinkWithPopupContentComponent {
  constructor(@Inject(POPUP_CONTENT) public content: any) {}
  headerText: string = this.content.headerText;
  isLoading: Observable<boolean> = this.content.isLoading;
  close() {} // Will be implemented by parent
}

@Component({
  selector: 'app-link-with-popup',
  template: `
    <div class="popup-trigger">
      <a (click)="togglePopup($event)" class="view-btn">
        {{linkText}}
      </a>
    </div>

    <!-- Capture the content -->
    <!-- <ng-template>
      <ng-content></ng-content>
    </ng-template> -->
  `,
  styles: [`
    .popup-trigger {
      display: inline-block;
    }

    .view-btn {
      padding: 5px 10px;
      cursor: pointer;
    }
  `]
})
export class LinkWithPopupComponent implements OnDestroy {
  private overlayRef: OverlayRef | null = null;

  @Input() headerText: string = '';
  @Input() linkText: string = '';
  @Input() isLoading: Observable<boolean> = of(false);
  @Output() popupOpen = new EventEmitter<void>();
  // Capture the projected content
  @ContentChild(TemplateRef) contentTemplate!: TemplateRef<any>;

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef,
    private injector: Injector
  ) {}

  private createInjector(): Injector {
    return Injector.create({
      parent: this.injector,
      providers: [
        {
          provide: POPUP_CONTENT,
          useValue: {
            template: this.contentTemplate,
            headerText: this.headerText,
            linkText: this.linkText,
            isLoading: this.isLoading
          }
        }
      ]
    });
  }

  togglePopup(event: MouseEvent) {
    event.stopPropagation();

    if (this.overlayRef) {
      this.closePopup();
    } else {
      this.openPopup(event);
    }
  }

  private openPopup(event: MouseEvent) {
    const button = event.target as HTMLElement;

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(button)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
            offsetY: 5
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
            offsetY: -5
          }
        ]),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop'
    });

    this.overlayRef.backdropClick().subscribe(() => this.closePopup());

    // Create portal with the injector that contains our content
    const portal = new ComponentPortal(
      LinkWithPopupContentComponent,
      null,
      this.createInjector()
    );

    const componentRef = this.overlayRef.attach(portal);
    componentRef.instance.close = () => this.closePopup();
    this.popupOpen.emit();
  }

  private closePopup() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  ngOnDestroy() {
    this.closePopup();
  }
}
